'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  id: 1,
  name: 'Priya Chandrasekaran',
  location: 'San Francisco, CA',
  quote: 'Genuinely transformed my skincare routine.',
  text: 'The Velvet Glow Serum is unlike anything I\'ve used before. My skin is noticeably more radiant after just two weeks. SweetSkyn by Kym\'s curation is impeccable — every product I\'ve tried has exceeded my expectations.',
  rating: 5,
  image: "/assets/images/template/rocket_gen_img_1bfddacc6-1772205842001.webp",
  alt: 'Priya, smiling woman with dark hair, warm natural light portrait'
},
{
  id: 2,
  name: 'Amara Okonkwo',
  location: 'New York, NY',
  image: "https://images.unsplash.com/photo-1645951249336-172f41b34321",
  alt: 'Amara, confident woman with natural hair, studio portrait',
  quote: '',
  text: '',
  rating: 5
},
{
  id: 3,
  name: 'Sofia Marchetti',
  location: 'Los Angeles, CA',
  image: "/assets/images/template/rocket_gen_img_1f17c10df-1772502066935.webp",
  alt: 'Sofia, woman with elegant features, soft natural light portrait',
  quote: '',
  text: '',
  rating: 5
},
{
  id: 4,
  name: 'Mei-Ling Zhao',
  location: 'Chicago, IL',
  image: "https://images.unsplash.com/photo-1681566993431-cd5697255bd9",
  alt: 'Mei-Ling, woman with bright smile, warm-toned portrait',
  quote: '',
  text: '',
  rating: 5
}];


export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.remove('reveal-hidden'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="reveal reveal-hidden text-center mb-16">
          <span className="section-label block mb-3">Voices of Our Community</span>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-foreground">
            Loved by thousands
          </h2>
        </div>

        {/* Overlapping portrait layout — inspired by Template 1 testimonials */}
        <div className="relative min-h-[500px] md:min-h-[560px] flex items-center justify-center">
          {/* Left portrait — rotated */}
          <div className="reveal reveal-hidden absolute left-0 md:left-4 top-1/2 -translate-y-1/2 -rotate-6 w-32 md:w-44 h-44 md:h-60 card-rounded overflow-hidden shadow-xl hidden sm:block">
            <AppImage
              src={testimonials?.[1]?.image}
              alt={testimonials?.[1]?.alt}
              fill
              sizes="176px"
              className="object-cover grayscale" />
            
          </div>
          <div className="reveal reveal-hidden absolute left-16 md:left-28 top-1/2 -translate-y-1/3 rotate-3 w-28 md:w-36 h-40 md:h-52 card-rounded overflow-hidden shadow-lg hidden md:block">
            <AppImage
              src={testimonials?.[2]?.image}
              alt={testimonials?.[2]?.alt}
              fill
              sizes="144px"
              className="object-cover grayscale" />
            
          </div>

          {/* Center quote card */}
          <div className="reveal reveal-hidden relative z-10 w-full max-w-md glass-card p-10 md:p-14 card-rounded shadow-2xl text-center mx-4 md:mx-0 border border-border">
            <div className="w-16 h-16 mx-auto mb-6 rounded-xl overflow-hidden border-2 border-white shadow-md">
              <AppImage
                src={testimonials?.[0]?.image}
                alt={testimonials?.[0]?.alt}
                width={64}
                height={64}
                className="object-cover w-full h-full" />
              
            </div>
            <h4 className="font-semibold text-foreground text-base mb-0.5">{testimonials?.[0]?.name}</h4>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-6">{testimonials?.[0]?.location}</p>
            <div className="flex justify-center gap-0.5 mb-6">
              {[1, 2, 3, 4, 5]?.map((s) =>
              <svg key={s} className="w-4 h-4 text-accent fill-current" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>
            <p className="font-display text-xl font-light text-foreground italic mb-4">
              &ldquo;{testimonials?.[0]?.quote}&rdquo;
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {testimonials?.[0]?.text}
            </p>
          </div>

          {/* Right portraits */}
          <div className="reveal reveal-hidden absolute right-16 md:right-28 top-1/2 -translate-y-1/4 -rotate-3 w-28 md:w-36 h-40 md:h-52 card-rounded overflow-hidden shadow-lg hidden md:block">
            <AppImage
              src={testimonials?.[3]?.image}
              alt={testimonials?.[3]?.alt}
              fill
              sizes="144px"
              className="object-cover grayscale" />
            
          </div>
          <div className="reveal reveal-hidden absolute right-0 md:right-4 top-1/2 -translate-y-1/2 rotate-6 w-32 md:w-44 h-44 md:h-60 card-rounded overflow-hidden shadow-xl hidden sm:block">
            <AppImage
              src={testimonials?.[2]?.image}
              alt={testimonials?.[2]?.alt}
              fill
              sizes="176px"
              className="object-cover grayscale" />
            
          </div>
        </div>
      </div>
    </section>);

}